import React, { useState, useEffect } from 'react';
import { IconButton } from '../ui';

/**
 * Carousel gambar (foto toko, gallery).
 * @param {Array} images - [{ src, alt }] atau [src string]
 * @param {number} intervalMs - auto-play (0 = mati)
 * @param {string} aspect - aspect ratio class, default aspect-video. Gunakan 'auto' agar area menyesuaikan ukuran gambar.
 * @param {string} objectFit - 'cover' | 'contain', untuk tampil full tanpa crop pakai 'contain'
 */
export default function ImageCarousel({ images = [], intervalMs = 0, aspect = 'aspect-video', objectFit = 'cover' }) {
  const [index, setIndex] = useState(0);
  const length = images.length;

  useEffect(() => {
    if (length <= 1 || !intervalMs) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % length), intervalMs);
    return () => clearInterval(t);
  }, [length, intervalMs]);

  if (!length) return null;

  const slide = images[index];
  const src = typeof slide === 'string' ? slide : slide?.src;
  const alt = typeof slide === 'string' ? '' : slide?.alt || 'Foto';

  const goPrev = () => setIndex((i) => (i - 1 + length) % length);
  const goNext = () => setIndex((i) => (i + 1) % length);

  const fitToImage = aspect === 'auto';

  return (
    <div
      className={`relative overflow-hidden rounded-xl bg-am-green-100 shadow-md ${fitToImage ? 'w-fit max-w-full' : 'w-full'}`}
      aria-label="Foto lokasi"
    >
      <div
        className={
          fitToImage
            ? 'inline-block min-w-0'
            : `w-full ${aspect} min-h-[200px]`
        }
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            className={
              fitToImage
                ? 'block max-h-[420px] sm:max-h-[520px] md:max-h-[600px] w-auto h-auto max-w-full object-contain'
                : `w-full h-full ${objectFit === 'contain' ? 'object-contain' : 'object-cover'}`
            }
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-am-green-100 to-am-accent/20 text-am-text-700 font-medium">
            {alt || `Foto ${index + 1}`}
          </div>
        )}
      </div>
      {length > 1 && (
        <>
          <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
            <IconButton variant="default" shape="round" size="sm" aria-label="Sebelumnya" onClick={goPrev}>‹</IconButton>
          </div>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
            <IconButton variant="default" shape="round" size="sm" aria-label="Berikutnya" onClick={goNext}>›</IconButton>
          </div>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === index ? 'bg-white scale-125' : 'bg-white/60'}`}
                aria-label={`Foto ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
